import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HardDrive, Cloud, Save, TestTube } from "lucide-react";
import { toast } from "sonner";

export default function SystemSettings() {
  const [form, setForm] = useState({
    backend: "fs", upload_dir: "", aws_access_key_id: "", aws_secret_access_key: "",
    aws_region: "eu-west-1", aws_s3_bucket: "", aws_s3_prefix: "docs/",
  });
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/system-settings/storage");
      setCurrent(data);
      setForm({
        backend: data.backend || "fs",
        upload_dir: data.upload_dir || "",
        aws_access_key_id: data.aws_access_key_id_masked || "",
        aws_secret_access_key: "",
        aws_region: data.aws_region || "eu-west-1",
        aws_s3_bucket: data.aws_s3_bucket || "",
        aws_s3_prefix: data.aws_s3_prefix || "docs/",
      });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Don't re-send masked key
      const payload = { ...form };
      if (payload.aws_access_key_id && payload.aws_access_key_id.includes("…")) delete payload.aws_access_key_id;
      if (!payload.aws_secret_access_key) delete payload.aws_secret_access_key;
      await api.put("/system-settings/storage", payload);
      toast.success("Configuración guardada");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/system-settings/storage/test");
      toast.success(`Storage OK (backend activo: ${data.backend})`);
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Ajustes del sistema"
        subtitle="Backend de almacenamiento de archivos. Por defecto, filesystem local; puedes migrar a S3 sin tocar código."
      />
      <Card className="p-8 rounded-sm border-[#E5E7EB] shadow-none max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#7B1826] flex items-center justify-center rounded-sm">
            {form.backend === "s3" ? <Cloud className="w-5 h-5 text-white" /> : <HardDrive className="w-5 h-5 text-white" />}
          </div>
          <div>
            <h2 className="font-display text-2xl">Almacenamiento de archivos</h2>
            <p className="text-xs text-neutral-500">Activo actualmente: <span className="font-mono">{current?.active_backend || "—"}</span></p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Backend</Label>
            <Select value={form.backend} onValueChange={(v) => setForm({ ...form, backend: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="storage-backend-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fs">Filesystem local</SelectItem>
                <SelectItem value="s3">Amazon S3 (o compatible)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.backend === "fs" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider">Directorio de subidas</Label>
              <Input value={form.upload_dir} onChange={(e) => setForm({ ...form, upload_dir: e.target.value })}
                placeholder="/var/www/vhosts/lexduo.cat/docs.lexduo.cat/uploads"
                data-testid="storage-fs-dir" className="rounded-sm mt-1 font-mono text-sm" />
              <p className="text-[11px] text-neutral-500 mt-1">Si lo dejas vacío, se usa el valor de la variable UPLOAD_DIR del .env</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">AWS Access Key ID</Label>
                  <Input value={form.aws_access_key_id} onChange={(e) => setForm({ ...form, aws_access_key_id: e.target.value })}
                    placeholder={current?.has_aws_keys ? "(actual oculta — escribe para cambiar)" : "AKIA..."}
                    data-testid="storage-aws-key" className="rounded-sm mt-1 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">AWS Secret Access Key</Label>
                  <Input type="password" value={form.aws_secret_access_key}
                    onChange={(e) => setForm({ ...form, aws_secret_access_key: e.target.value })}
                    placeholder={current?.has_aws_keys ? "(guardada)" : "..."}
                    data-testid="storage-aws-secret" className="rounded-sm mt-1 font-mono text-xs" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Región</Label>
                  <Input value={form.aws_region} onChange={(e) => setForm({ ...form, aws_region: e.target.value })}
                    placeholder="eu-west-1" className="rounded-sm mt-1" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Bucket</Label>
                  <Input value={form.aws_s3_bucket} onChange={(e) => setForm({ ...form, aws_s3_bucket: e.target.value })}
                    placeholder="lexduo-storage" className="rounded-sm mt-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Prefijo (key prefix)</Label>
                <Input value={form.aws_s3_prefix} onChange={(e) => setForm({ ...form, aws_s3_prefix: e.target.value })}
                  placeholder="docs/" className="rounded-sm mt-1 font-mono text-sm" />
              </div>
            </>
          )}

          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="storage-save-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
              <Save className="w-4 h-4 mr-2" />{saving ? "Guardando…" : "Guardar"}
            </Button>
            <Button onClick={test} disabled={testing} variant="outline" className="rounded-sm" data-testid="storage-test-button">
              <TestTube className="w-4 h-4 mr-2" />Probar
            </Button>
          </div>
        </div>
      </Card>
    </PageContainer>
  );
}
